import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';
import { toast } from '../components/ui/Toast';

// ── DTOs ──
export interface InsumoDTO {
  id: string;
  detalle: string;
  categoria: 'PINTURA' | 'IMPERMEABILIZACION' | 'GYPSUM';
  presentacion: 'UNIDAD' | 'GALON' | 'LITRO' | 'KILO' | 'CANECA';
  precioUnitario: number;
  isActive: boolean;
}

export interface CreateInsumoInput {
  detalle: string;
  categoria: 'PINTURA' | 'IMPERMEABILIZACION' | 'GYPSUM';
  presentacion: 'UNIDAD' | 'GALON' | 'LITRO' | 'KILO' | 'CANECA';
  precioUnitario: number;
}

export interface UpdateInsumoInput extends Partial<CreateInsumoInput> {
  isActive?: boolean;
}

// ── List ──
export function useInsumos() {
  return useQuery({
    queryKey: queryKeys.insumos.list(),
    queryFn: async () => {
      const response = await api.get<{ data: InsumoDTO[] }>('/insumos');
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ── Create ──
export function useCreateInsumo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateInsumoInput) => {
      const response = await api.post<{ data: InsumoDTO }>('/insumos', data);
      return response.data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.insumos.all });
      toast('success', `Insumo "${data.detalle}" creado exitosamente`);
    },
  });
}

// ── Update ──
export function useUpdateInsumo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateInsumoInput }) => {
      const response = await api.put<{ data: InsumoDTO }>(`/insumos/${id}`, data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.insumos.all });
      toast('success', 'Insumo actualizado');
    },
  });
}

// ── Delete (soft) ──
export function useDeleteInsumo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete<{ data: InsumoDTO }>(`/insumos/${id}`);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.insumos.all });
      toast('success', 'Insumo desactivado');
    },
  });
}
